import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FolderTree, Folder, FolderPlus, FolderInput, Plus, Search, Trash2, Pencil,
  FileDown, X, ChevronRight, ChevronDown, Users, ArrowLeft, Check, UserPlus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { API } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, Input, Select, Textarea, Field, PageHeader, Panel } from "@/components/common";
import RecordPicker from "@/components/RecordPicker";
import Modal from "@/components/Modal";
import ImportWizard from "@/components/ImportWizard";
import { CreateEntityModal } from "@/pages/Entities";

const impColor = { critical: "disputed", high: "confirmed", medium: "uncertain", low: "candidate", unassessed: "candidate" };

export default function Groups() {
  const { workspaceId, canEdit, isOwner } = useAuth();
  const [folders, setFolders] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState("all"); // "all" | folderId | "top"
  const [openFolders, setOpenFolders] = useState({});
  const [params, setParams] = useSearchParams();
  const activeGroupId = params.get("group");

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const [f, g] = await Promise.all([
      api.get(`/folders?workspace_id=${workspaceId}`),
      api.get(`/groups?workspace_id=${workspaceId}`),
    ]);
    setFolders(f.data.items);
    setGroups(g.data.items);
    setLoading(false);
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const childFolders = useMemo(() => {
    const m = {};
    folders.forEach((f) => { (m[f.parent_id || "root"] = m[f.parent_id || "root"] || []).push(f); });
    return m;
  }, [folders]);

  const visibleGroups = useMemo(() => {
    if (selectedFolder === "all") return groups;
    if (selectedFolder === "top") return groups.filter((g) => !g.folder_id);
    return groups.filter((g) => g.folder_id === selectedFolder);
  }, [groups, selectedFolder]);

  const [modal, setModal] = useState(null); // {type, ...}

  if (loading) return <Loading />;

  if (activeGroupId) {
    return <GroupView groupId={activeGroupId} workspaceId={workspaceId} canEdit={canEdit} isOwner={isOwner}
      onBack={() => { params.delete("group"); setParams(params); }} onChanged={load} />;
  }

  const renderTree = (parentId, depth) => {
    const kids = childFolders[parentId || "root"] || [];
    return kids.map((f) => {
      const hasKids = (childFolders[f.id] || []).length > 0;
      const open = openFolders[f.id];
      const count = groups.filter((g) => g.folder_id === f.id).length;
      return (
        <div key={f.id}>
          <div className="flex items-center gap-1 pr-2 py-1.5 group cursor-pointer transition-colors duration-200"
            style={{ paddingLeft: 8 + depth * 14, background: selectedFolder === f.id ? "var(--si-surface-hover)" : "transparent" }}
            data-testid={`folder-row-${f.id}`} onClick={() => setSelectedFolder(f.id)}>
            <button onClick={(e) => { e.stopPropagation(); setOpenFolders((o) => ({ ...o, [f.id]: !o[f.id] })); }}
              style={{ width: 16, color: "var(--si-text-2)" }} data-testid={`folder-toggle-${f.id}`}>
              {hasKids ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span />}
            </button>
            <Folder size={14} style={{ color: "var(--si-teal-light)" }} />
            <span className="text-sm flex-1 truncate" style={{ color: "var(--si-text)" }}>{f.name}</span>
            <span className="si-label">{count}</span>
            {canEdit && (
              <span className="hidden group-hover:flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); setModal({ type: "folder-edit", folder: f }); }} title="Rename / move" style={{ color: "var(--si-text-2)" }} data-testid={`folder-edit-${f.id}`}><Pencil size={12} /></button>
                <button onClick={(e) => { e.stopPropagation(); setModal({ type: "folder-delete", folder: f }); }} title="Delete folder" style={{ color: "var(--si-text-2)" }} data-testid={`folder-del-${f.id}`}><Trash2 size={12} /></button>
              </span>
            )}
          </div>
          {open && renderTree(f.id, depth + 1)}
        </div>
      );
    });
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader title="Groups & Folders" sub="Organize contacts and entities into groups. Membership references your canonical records — it never duplicates a contact."
        actions={canEdit && (
          <div className="flex gap-2">
            <Btn variant="secondary" data-testid="new-folder-btn" onClick={() => setModal({ type: "folder-new" })}><FolderPlus size={15} /> New folder</Btn>
            <Btn variant="secondary" data-testid="import-csv-btn" onClick={() => setModal({ type: "import" })}><FileDown size={15} /> Import CSV / Excel</Btn>
            <Btn variant="primary" data-testid="new-group-btn" onClick={() => setModal({ type: "group-new" })}><Plus size={15} /> New group</Btn>
          </div>
        )} />

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-5">
        {/* Folder tree */}
        <Panel className="py-2 self-start">
          <button onClick={() => setSelectedFolder("all")} data-testid="folder-all"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-200"
            style={{ background: selectedFolder === "all" ? "var(--si-surface-hover)" : "transparent", color: "var(--si-text)" }}>
            <FolderTree size={14} style={{ color: "var(--si-silver)" }} /> All groups <span className="si-label ml-auto">{groups.length}</span>
          </button>
          <button onClick={() => setSelectedFolder("top")} data-testid="folder-top"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-200"
            style={{ background: selectedFolder === "top" ? "var(--si-surface-hover)" : "transparent", color: "var(--si-text-2)" }}>
            <Folder size={14} /> Unfiled <span className="si-label ml-auto">{groups.filter((g) => !g.folder_id).length}</span>
          </button>
          <div style={{ borderTop: "1px solid var(--si-border)" }} className="mt-1 pt-1">
            {folders.length === 0 ? <div className="si-label px-3 py-2">No folders yet.</div> : renderTree(null, 0)}
          </div>
        </Panel>

        {/* Group list */}
        <div>
          {visibleGroups.length === 0 ? (
            <EmptyState icon={Users} title="No groups here" hint="Create a group to start organizing contacts." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {visibleGroups.map((g) => {
                const folder = folders.find((f) => f.id === g.folder_id);
                return (
                  <Panel key={g.id} className="p-4 cursor-pointer transition-colors duration-200"
                    data-testid={`group-card-${g.id}`} onClick={() => { params.set("group", g.id); setParams(params); }}
                    style={{}}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-base font-medium truncate" style={{ color: "var(--si-text)" }}>{g.name}</div>
                        {folder && <div className="si-label mt-0.5 flex items-center gap-1"><Folder size={10} /> {folder.name}</div>}
                        {g.purpose && <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--si-text-2)" }}>{g.purpose}</div>}
                      </div>
                      <Badge value="candidate">{g.member_count} {g.member_count === 1 ? "member" : "members"}</Badge>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setModal({ type: "group-edit", group: g })} title="Edit group" style={{ color: "var(--si-text-2)" }} data-testid={`group-edit-${g.id}`}><Pencil size={14} /></button>
                        <button onClick={() => setModal({ type: "group-delete", group: g })} title="Delete group" style={{ color: "var(--si-text-2)" }} data-testid={`group-del-${g.id}`}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </Panel>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {modal?.type === "folder-new" && <FolderModal workspaceId={workspaceId} folders={folders} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "folder-edit" && <FolderModal workspaceId={workspaceId} folders={folders} edit={modal.folder} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "folder-delete" && <DeleteFolderModal workspaceId={workspaceId} folder={modal.folder} onClose={() => setModal(null)} onDeleted={() => { setModal(null); setSelectedFolder("all"); load(); }} />}
      {modal?.type === "group-new" && <GroupModal workspaceId={workspaceId} folders={folders} defaultFolder={["all", "top"].includes(selectedFolder) ? "" : selectedFolder} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "group-edit" && <GroupModal workspaceId={workspaceId} folders={folders} edit={modal.group} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "group-delete" && <DeleteGroupModal workspaceId={workspaceId} group={modal.group} onClose={() => setModal(null)} onDeleted={() => { setModal(null); load(); }} />}
      {modal?.type === "import" && (
        <ImportWizard workspaceId={workspaceId} folders={folders} existingGroups={groups}
          onClose={() => setModal(null)}
          onComplete={(groupId) => { load(); if (groupId) { params.set("group", groupId); setParams(params); } }} />
      )}
    </div>
  );
}

function FolderModal({ workspaceId, folders, edit, onClose, onSaved }) {
  const [f, setF] = useState({ name: edit?.name || "", parent_id: edit?.parent_id || "", description: edit?.description || "" });
  const [busy, setBusy] = useState(false);
  const options = folders.filter((x) => x.id !== edit?.id);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (edit) await api.put(`/folders/${edit.id}?workspace_id=${workspaceId}`, { name: f.name, parent_id: f.parent_id || "", description: f.description });
      else await api.post(`/folders?workspace_id=${workspaceId}`, { name: f.name, parent_id: f.parent_id || null, description: f.description });
      toast.success(edit ? "Folder updated" : "Folder created"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={edit ? "Edit folder" : "New folder"} testid="folder-modal">
      <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus data-testid="folder-name-input" /></Field>
      <Field label="Parent folder (optional)" hint="Leave blank for a top-level folder."><Select value={f.parent_id} onChange={(e) => setF({ ...f, parent_id: e.target.value })} data-testid="folder-parent-select"><option value="">— top level —</option>{options.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></Field>
      <Field label="Description (optional)"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="folder-save">{busy ? "Saving…" : edit ? "Save folder" : "Create folder"}</Btn>
    </Modal>
  );
}

function DeleteFolderModal({ workspaceId, folder, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/folders/${folder.id}/delete-preview?workspace_id=${workspaceId}`).then((r) => setPreview(r.data)).catch(() => {}); }, [folder.id, workspaceId]);
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/folders/${folder.id}?workspace_id=${workspaceId}`); toast.success("Folder deleted"); onDeleted(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete folder" testid="folder-delete-modal">
      <p className="text-sm mb-3" style={{ color: "var(--si-text-2)" }}>Delete <strong style={{ color: "var(--si-text)" }}>{folder.name}</strong>?</p>
      {preview && (
        <div className="text-sm space-y-1 mb-3">
          <div className="flex justify-between"><span>Subfolders removed</span><span className="font-mono">{preview.counts.subfolders}</span></div>
          <div className="flex justify-between"><span>Groups removed</span><span className="font-mono">{preview.counts.groups}</span></div>
          <div className="flex justify-between"><span>Group placements removed</span><span className="font-mono">{preview.counts.memberships_removed}</span></div>
        </div>
      )}
      <p className="text-xs mb-4" style={{ color: "var(--si-warning)" }}>{preview?.note || "No contact records are deleted — only their group placement is removed."}</p>
      <div className="flex gap-2 justify-end">
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn variant="danger" onClick={del} disabled={busy} data-testid="folder-delete-confirm"><Trash2 size={15} /> {busy ? "Deleting…" : "Delete folder"}</Btn>
      </div>
    </Modal>
  );
}

function GroupModal({ workspaceId, folders, edit, defaultFolder = "", onClose, onSaved }) {
  const [f, setF] = useState({
    name: edit?.name || "", folder_id: edit?.folder_id || defaultFolder || "",
    description: edit?.description || "", purpose: edit?.purpose || "", target_criteria: edit?.target_criteria || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      if (edit) await api.put(`/groups/${edit.id}?workspace_id=${workspaceId}`, { name: f.name, folder_id: f.folder_id || "", description: f.description, purpose: f.purpose, target_criteria: f.target_criteria });
      else await api.post(`/groups?workspace_id=${workspaceId}`, { name: f.name, folder_id: f.folder_id || null, description: f.description, purpose: f.purpose, target_criteria: f.target_criteria });
      toast.success(edit ? "Group updated" : "Group created"); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={edit ? "Edit group" : "New group"} testid="group-modal">
      <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus data-testid="group-name-input" /></Field>
      <Field label="Folder (optional)"><Select value={f.folder_id} onChange={(e) => setF({ ...f, folder_id: e.target.value })} data-testid="group-folder-select"><option value="">— unfiled —</option>{folders.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></Field>
      <Field label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="Purpose"><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></Field>
      <Field label="Target criteria (optional)"><Input value={f.target_criteria} onChange={(e) => setF({ ...f, target_criteria: e.target.value })} placeholder="e.g. broker-dealers in PA" /></Field>
      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="group-save">{busy ? "Saving…" : edit ? "Save group" : "Create group"}</Btn>
    </Modal>
  );
}

function DeleteGroupModal({ workspaceId, group, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { const { data } = await api.delete(`/groups/${group.id}?workspace_id=${workspaceId}`); toast.success(`Group deleted (${data.memberships_removed} placements removed)`); onDeleted(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Delete group" testid="group-delete-modal">
      <p className="text-sm mb-3" style={{ color: "var(--si-text-2)" }}>Delete <strong style={{ color: "var(--si-text)" }}>{group.name}</strong> ({group.member_count} members)?</p>
      <p className="text-xs mb-4" style={{ color: "var(--si-warning)" }}>The group is removed. No contact records are deleted or archived — only their placement in this group is removed.</p>
      <div className="flex gap-2 justify-end">
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn variant="danger" onClick={del} disabled={busy} data-testid="group-delete-confirm"><Trash2 size={15} /> {busy ? "Deleting…" : "Delete group"}</Btn>
      </div>
    </Modal>
  );
}

function GroupView({ groupId, workspaceId, canEdit, isOwner, onBack, onChanged }) {
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const [sel, setSel] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDesc, setEditDesc] = useState(false);

  const loadGroup = useCallback(async () => {
    const { data } = await api.get(`/groups/${groupId}?workspace_id=${workspaceId}`);
    setGroup(data);
  }, [groupId, workspaceId]);
  const loadMembers = useCallback(async () => {
    const p = new URLSearchParams({ workspace_id: workspaceId, q, sort, limit: 200 });
    const { data } = await api.get(`/groups/${groupId}/members?${p}`);
    setData(data);
  }, [groupId, workspaceId, q, sort]);
  useEffect(() => { loadGroup(); }, [loadGroup]);
  useEffect(() => { const t = setTimeout(loadMembers, 250); return () => clearTimeout(t); }, [loadMembers]);

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const toggleAll = () => {
    if (!data) return;
    if (selectedIds.length === data.items.length) setSel({});
    else { const n = {}; data.items.forEach((m) => (n[m.id] = true)); setSel(n); }
  };
  const removeOne = async (eid) => {
    await api.delete(`/groups/${groupId}/members/${eid}?workspace_id=${workspaceId}`);
    toast.success("Removed from group"); loadMembers(); loadGroup(); onChanged && onChanged();
  };
  const bulkRemove = async () => {
    const { data: r } = await api.post(`/groups/${groupId}/members/bulk-remove?workspace_id=${workspaceId}`, { entity_ids: selectedIds });
    toast.success(`${r.removed} removed from group (contacts preserved)`); setSel({}); loadMembers(); loadGroup(); onChanged && onChanged();
  };

  if (!group) return <Loading />;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm mb-4" style={{ color: "var(--si-text-2)" }} data-testid="group-back"><ArrowLeft size={15} /> Groups & Folders</button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-medium" style={{ color: "var(--si-text)" }}>{group.name}</h1>
            <Badge value="candidate">{group.member_count} members</Badge>
          </div>
          {(group.folder_path || []).length > 0 && (
            <div className="si-label mt-1 flex items-center gap-1"><Folder size={11} /> {group.folder_path.map((p) => p.name).join(" / ")}</div>
          )}
          {group.purpose && <div className="text-sm mt-1.5" style={{ color: "var(--si-text-2)" }}>{group.purpose}</div>}
          {group.target_criteria && <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>Target: {group.target_criteria}</div>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && <Btn variant="primary" onClick={() => setAddOpen(true)} data-testid="group-add-existing"><UserPlus size={15} /> Add existing</Btn>}
          {canEdit && <Btn variant="secondary" onClick={() => setCreateOpen(true)} data-testid="group-create-add"><Plus size={15} /> Create & add</Btn>}
          <Btn variant="ghost" onClick={() => window.open(`${API}/reports/group/${groupId}.pdf?workspace_id=${workspaceId}`, "_blank")} data-testid="group-pdf"><FileDown size={15} /> Roster PDF</Btn>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--si-text-2)" }} />
          <Input data-testid="group-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name, org, phone, email, tags, notes…" style={{ paddingLeft: 30 }} />
        </div>
        <Select value={sort} onChange={(e) => setSort(e.target.value)} style={{ width: "auto" }} data-testid="group-sort">
          <option value="name">Name A–Z</option><option value="-name">Name Z–A</option>
          <option value="importance">Importance</option><option value="type">Type</option>
        </Select>
        {canEdit && selectedIds.length > 0 && <Btn variant="danger" onClick={bulkRemove} data-testid="group-bulk-remove"><X size={14} /> Remove {selectedIds.length} from group</Btn>}
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? (
        <EmptyState icon={Users} title={q ? "No members match your search" : "No members yet"} hint={q ? "Adjust your search." : "Add existing contacts or create a new one."} />
      ) : (
        <div style={{ border: "1px solid var(--si-border)", borderRadius: 2, overflow: "hidden" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--si-surface)" }}>
              <tr style={{ borderBottom: "1px solid var(--si-border)" }}>
                {canEdit && <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={selectedIds.length === data.items.length} onChange={toggleAll} data-testid="group-select-all" /></th>}
                {["Name", "Type", "Organization", "Importance", "Spider networks", "Next follow-up", "Note", ""].map((h) => <th key={h} className="text-left px-3 py-2.5 si-label font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m.id} data-testid={`group-member-${m.id}`} style={{ borderBottom: "1px solid var(--si-border)" }}>
                  {canEdit && <td className="px-3 py-2.5"><input type="checkbox" checked={!!sel[m.id]} onChange={(e) => setSel((s) => ({ ...s, [m.id]: e.target.checked }))} data-testid={`group-select-${m.id}`} /></td>}
                  <td className="px-3 py-2.5"><button className="text-left hover:underline" style={{ color: "var(--si-text)" }} onClick={() => navigate(`/app/entities/${m.id}`)} data-testid={`group-member-link-${m.id}`}>{m.name}</button></td>
                  <td className="px-3 py-2.5 font-mono text-xs" style={{ color: "var(--si-text-2)" }}>{m.type}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: "var(--si-text-2)" }}>{m.organization || "—"}</td>
                  <td className="px-3 py-2.5"><Badge value={impColor[m.importance] || "candidate"}>{m.importance}</Badge></td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: "var(--si-text-2)" }}>{(m.spider_memberships || []).join(", ") || "—"}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: "var(--si-text-2)" }}>{m.next_follow_up ? `${(m.next_follow_up.due_date || "").slice(0, 10)}` : "—"}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: "var(--si-text-2)", maxWidth: 160 }}>{m.note || "—"}</td>
                  <td className="px-3 py-2.5">{canEdit && <button onClick={() => removeOne(m.id)} title="Remove from group" style={{ color: "var(--si-text-2)" }} data-testid={`group-remove-${m.id}`}><X size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && <AddMembersModal groupId={groupId} workspaceId={workspaceId} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); loadMembers(); loadGroup(); onChanged && onChanged(); }} />}
      {createOpen && <CreateEntityModal workspaceId={workspaceId} defaultType="person" defaultGroupId={groupId}
        contextLabel="Will be added to this group"
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); loadMembers(); loadGroup(); onChanged && onChanged(); }} />}
    </div>
  );
}

function AddMembersModal({ groupId, workspaceId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const search = useCallback(async () => {
    const { data } = await api.get(`/entities?workspace_id=${workspaceId}&search=${encodeURIComponent(q)}&limit=25`);
    setResults(data.items);
  }, [q, workspaceId]);
  useEffect(() => { const t = setTimeout(search, 250); return () => clearTimeout(t); }, [search]);
  const pickedIds = Object.keys(picked).filter((k) => picked[k]);
  const quickAdd = (rec) => {
    if (!rec) return;
    setResults((prev) => (prev.some((r) => r.id === rec.id) ? prev : [rec, ...prev]));
    setPicked((p) => ({ ...p, [rec.id]: true }));
  };
  const save = async () => {
    if (pickedIds.length === 0) { setErr("Add to which group? Select at least one contact, or use Me."); return; }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/members?workspace_id=${workspaceId}`, { entity_ids: pickedIds, note });
      toast.success(`${data.added} added${data.skipped_existing ? `, ${data.skipped_existing} already in group` : ""}`); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Add existing entities" width={600} testid="add-members-modal">
      <div className="mb-3"><RecordPicker workspaceId={workspaceId} label="Quick add — yourself (Me) or jump to a contact" value={null} onChange={quickAdd} allowMe allowUnset testid="group-quick-add" /></div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search contacts by name, alias, tag…" autoFocus data-testid="add-members-search" />
      <div className="mt-2 max-h-72 overflow-y-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
        {results.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: "var(--si-text-2)" }}>No matches.</div> : results.map((r) => (
          <label key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer" style={{ borderBottom: "1px solid var(--si-border)", color: "var(--si-text)" }} data-testid={`add-member-opt-${r.id}`}>
            <input type="checkbox" checked={!!picked[r.id]} onChange={(e) => setPicked((p) => ({ ...p, [r.id]: e.target.checked }))} />
            {r.name} <span className="si-label ml-1">{r.type}</span>
          </label>
        ))}
      </div>
      <Field label="Membership note (optional, applied to all selected)" hint="Why do these belong in this group?"><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="add-members-note" /></Field>
      {err && <div className="text-xs mb-2" style={{ color: "var(--si-danger)" }} data-testid="add-members-error">{err}</div>}
      <Btn variant="primary" className="w-full" onClick={save} disabled={busy} data-testid="add-members-save">{busy ? "Adding…" : `Add ${pickedIds.length || ""} to group`}</Btn>
    </Modal>
  );
}
